import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import axios from 'axios';
import { baseUrl } from '../components/BaseUrl';

// SVG Icon Components
const MailIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
    <polyline points="22,6 12,13 2,6"></polyline>
  </svg>
);

const ArrowLeftIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="19" y1="12" x2="5" y2="12"></line>
    <polyline points="12 19 5 12 12 5"></polyline>
  </svg>
);

const ShieldIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
  </svg>
);

const ForgetPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const navigate = useNavigate();

  const validate = async (e) => {
    e.preventDefault();
    
    if (!email) {
      Swal.fire({
        title: "Email Required",
        text: "Please enter your email address",
        icon: "warning",
        confirmButtonText: "OK",
        backdrop: `rgba(143, 70, 0, 0.3)`
      });
      return;
    }
    
    if (!/\S+@\S+\.\S+/.test(email)) {
      Swal.fire({
        title: "Invalid Email",
        text: "Please enter a valid email address",
        icon: "warning",
        confirmButtonText: "OK",
        backdrop: `rgba(143, 70, 0, 0.3)`
      });
      return;
    }
    
    await checkEmail();
  };

  const checkEmail = async () => {
    setLoading(true);
    setDisabled(true);
    
    try {
      const userData = { email };
      const response = await axios.post(`${baseUrl}check-email.php`, userData);
      
      if (response.data.status === 0) {
        Swal.fire({
          title: "Email Not Found",
          text: "This email is not registered in our system",
          icon: "error",
          confirmButtonText: "OK",
          backdrop: `rgba(143, 70, 0, 0.3)`
        });
      } else if (response.data.status === 1) {
        Swal.fire({
          title: "Email Verified!",
          text: "Redirecting you to password reset page",
          icon: "success",
          timer: 1500,
          showConfirmButton: false,
          backdrop: `rgba(34, 139, 34, 0.3)`
        });
        
        setTimeout(() => {
          navigate(`/change-password/${email}`);
        }, 1500);
      }
    } catch (error) {
      Swal.fire({
        title: "Connection Error",
        text: "Unable to connect to server. Please try again later.",
        icon: "error",
        confirmButtonText: "OK",
        backdrop: `rgba(143, 70, 0, 0.3)`
      });
    } finally {
      setLoading(false);
      setDisabled(false);
    }
  };

  return (
    <div className="forgot-password-container">
      <div className="forgot-password-card">
        <div className="card-content">
          <div className="icon-container">
            <div className="icon-circle">
              <ShieldIcon />
            </div>
          </div>
          
          <h1 className="title">Reset Your Password</h1>
          
          <p className="subtitle">
            Enter your email address and we'll send you a link to reset your password.
          </p>
          
          <form onSubmit={validate}>
            <div className="form-group">
              <label htmlFor="email" className="label">
                Email Address
              </label>
              <div className="input-container">
                <div className="input-icon">
                <MailIcon />
                </div>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input-field"
                  placeholder="you@example.com"
                />
              </div>
            </div>
            
            <button
              type="submit"
              disabled={disabled}
              className={`submit-button ${disabled ? 'disabled' : ''}`}
            >
              {loading ? (
                <div className="loading-container">
                  <div className="spinner"></div>
                  <span className="loading-text">Processing...</span>
                </div>
              ) : (
                'Send Reset Link'
              )}
            </button>
          </form>
          
          <div className="back-to-login">
            <Link to="/login" className="back-link">
              <ArrowLeftIcon />
              <span className="back-text">Back to login</span>
            </Link>
          </div>
        </div>
        
        <div className="card-footer">
          <div className="footer-content">
            <span>&copy; {new Date().getFullYear()} Codeflare</span>
            <div className="footer-links">
              <a href="https://codeflarelimited.com/privacy-policy">Privacy</a>
              <a href="https://codeflarelimited.com/terms">Terms</a>
            </div>
          </div>
        </div>
      </div>
      
      <div className="support-link">
        <p>
          Need help? <a href="https://codeflarelimited.com/contact" target="_blank" rel="noopener noreferrer">Contact support</a>
        </p>
      </div>
      
      <style jsx>{`
        .forgot-password-container {
          min-height: 100vh;
          background: linear-gradient(135deg, #e0f7fa 0%, #bbdefb 100%);
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          padding: 20px;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
        }
        
        .forgot-password-card {
          width: 100%;
          max-width: 440px;
          background: white;
          border-radius: 16px;
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
          overflow: hidden;
          margin-bottom: 20px;
        }
        
        .card-content {
          padding: 30px;
        }
        
        .icon-container {
          display: flex;
          justify-content: center;
          margin-bottom: 20px;
        }
        
        .icon-circle {
          background: #e8f5e9;
          padding: 16px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .icon-circle svg {
          color: #4caf50;
        }
        
        .title {
          font-size: 24px;
          font-weight: 700;
          text-align: center;
          color: #2d3748;
          margin-bottom: 8px;
        }
        
        .subtitle {
          color: #718096;
          text-align: center;
          margin-bottom: 24px;
          line-height: 1.5;
        }
        
        .form-group {
          margin-bottom: 20px;
        }
        
        .label {
          display: block;
          font-size: 14px;
          font-weight: 500;
          color: #4a5568;
          margin-bottom: 6px;
        }
        
        .input-container {
          position: relative;
        }
        
        .input-icon {
          position: absolute;
          top: 10;
          bottom: 0;
          left: 12px;
          display: flex;
          align-items: center;
          color: #a0aec0;
        }
        
        .input-field {
          width: 100%;
          padding: 12px 12px 12px 40px;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          font-size: 16px;
          transition: all 0.2s;
          box-sizing: border-box;
        }
        
        .input-field:focus {
          outline: none;
          border-color: #4299e1;
          box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.2);
        }
        
        .submit-button {
          width: 100%;
          padding: 12px;
          background: #4299e1;
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: background 0.2s;
          margin-top: 10px;
        }
        
        .submit-button:hover:not(.disabled) {
          background: #3182ce;
        }
        
        .submit-button.disabled {
          background: #a0aec0;
          cursor: not-allowed;
        }
        
        .loading-container {
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .spinner {
          width: 18px;
          height: 18px;
          border: 2px solid transparent;
          border-top: 2px solid white;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-right: 8px;
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        .loading-text {
          color: white;
        }
        
        .back-to-login {
          margin-top: 24px;
          text-align: center;
        }
        
        .back-link {
          display: inline-flex;
          align-items: center;
          color: #4299e1;
          text-decoration: none;
          font-size: 14px;
          font-weight: 500;
          transition: color 0.2s;
        }
        
        .back-link:hover {
          color: #3182ce;
        }
        
        .back-text {
          margin-left: 6px;
        }
        
        .card-footer {
          background: #f7fafc;
          padding: 16px 30px;
          border-top: 1px solid #e2e8f0;
        }
        
        .footer-content {
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: 12px;
          color: #718096;
        }
        
        .footer-links {
          display: flex;
          gap: 16px;
        }
        
        .footer-links a {
          color: #718096;
          text-decoration: none;
          transition: color 0.2s;
        }
        
        .footer-links a:hover {
          color: #4299e1;
        }
        
        .support-link {
          text-align: center;
        }
        
        .support-link p {
          font-size: 14px;
          color: #718096;
        }
        
        .support-link a {
          color: #4299e1;
          text-decoration: none;
        }
        
        .support-link a:hover {
          text-decoration: underline;
        }
        
        @media (max-width: 480px) {
          .forgot-password-card {
            border-radius: 12px;
          }
          
          .card-content {
            padding: 20px;
          }
          
          .footer-content {
            flex-direction: column;
            gap: 8px;
          }
        }
      `}</style>
    </div>
  );
};

export default ForgetPassword;